import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { SupportPill, SupportIconButton } from './SupportJar';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { okToLeave } from '../../lib/unsavedChanges';

// ── SVG Icons (outline style, matching screenshot) ────────────────────────────
const IconSearch = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const IconChat = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
const IconUser = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconSettings = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);
const IconSignOut = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const IconPen = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9"/>
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
  </svg>
);

export default function Navbar() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  // Every way off the page asks first while something on it is not saved (ticket 118, src/lib/unsavedChanges.js).
  const go = path => { if (okToLeave()) navigate(path); };
  const [menuOpen, setMenuOpen] = useState(false);
  const [profile, setProfile]   = useState(null);

  // Fetch display name + avatar
  useEffect(() => {
    if (!user) return;
    supabase
      .from('user_profiles')
      .select('username, display_name, avatar_url')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => { if (data) setProfile(data); })
      .catch(err => console.error('Navbar profile fetch failed:', err.message));
  }, [user?.id]);

  useEffect(() => {
    setMenuOpen(false);
  }, [navigate]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleSignOut = async () => {
    if (!okToLeave()) return;
    await signOut();
    setMenuOpen(false);
    navigate('/discover');
  };

  const displayName = profile?.display_name || profile?.username || user?.email?.split('@')[0] || 'Reader';
  const initial     = displayName[0]?.toUpperCase() || 'R';

  const navLinkClass = ({ isActive }) =>
    isActive
      ? 'text-amber-700 text-sm font-semibold border-b-2 border-amber-600 pb-1'
      : 'text-stone-400 text-sm hover:text-stone-600 transition-colors';

  const NAV_ITEMS = [
    { label: 'Matchmaker',     path: '/discover',   Icon: IconSearch   },
    { label: 'Readers Corner', path: '/community',  Icon: IconChat     },
    { label: 'Profile',        path: '/profile',    Icon: IconUser     },
    { label: 'Settings',       path: '/settings',   Icon: IconSettings },
  ];

  return (
    <>
      {/* ── Sticky header ─────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-stone-200 sticky top-0" style={{ zIndex: 10002 }}>
        {/* Horizontal padding steps up rather than jumping straight to 80px:
            at 1024px the logo, nav and account groups were touching with 80px
            either side — no overflow, but no breathing room either. */}
        <div className="mx-auto px-[20px] md:px-[32px] xl:px-[80px] py-3 flex items-center justify-between">

          {/* Logo */}
          <div className="flex items-center gap-4">
            <button onClick={() => go('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/logo.png" alt="Reanlo" style={{ width: 32, height: 32, objectFit: 'contain' }} />
              <span className="font-bold text-stone-800 text-lg" style={{ fontFamily: 'var(--font-display)' }}>Reanlo</span>
            </button>
            <a
              href="https://reanloforauthors.com"
              target="_blank" rel="noopener noreferrer"
              className="hidden md:block"
              style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--brand-rust)', textDecoration: 'none', whiteSpace: 'nowrap', borderLeft: '1px solid var(--brand-sand)', paddingLeft: 'var(--space-lg)' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Are you an author? →
            </a>
          </div>

          {/* Desktop nav.
              gap-6 rather than gap-8: the support pill on the right needs the
              room, and at gap-8 the two groups met in the middle at 1280px. */}
          <nav className="hidden md:flex items-center gap-6">
            <NavLink to="/discover" end className={navLinkClass} onClick={e => { if (!okToLeave()) e.preventDefault(); }}>Matchmaker</NavLink>
            <button onClick={() => go('/community')} className="text-stone-400 text-sm hover:text-stone-600 transition-colors bg-transparent border-none cursor-pointer whitespace-nowrap">Reader's Corner</button>
            <button onClick={() => go('/profile')}   className="text-stone-400 text-sm hover:text-stone-600 transition-colors bg-transparent border-none cursor-pointer">Profile</button>
            <button onClick={() => go('/settings')}  className="text-stone-400 text-sm hover:text-stone-600 transition-colors bg-transparent border-none cursor-pointer">Settings</button>
          </nav>

          {/* Desktop auth. The support pill sits before the avatar, with a
              slightly wider gap than the items inside this group so it reads as
              its own thing rather than part of the account controls. */}
          <div className="hidden md:flex items-center gap-4">
            {/* Full label only where there's room for it. Below 1280px the
                long version pushes the nav into the logo. */}
            <span className="hidden xl:inline-flex"><SupportPill /></span>
            <span className="inline-flex xl:hidden"><SupportPill compact /></span>
            {user ? (
              <>
                <button onClick={() => go('/profile')} className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 text-sm font-bold hover:bg-amber-300 transition-colors">
                  {initial}
                </button>
                <button onClick={handleSignOut} className="text-sm text-stone-400 hover:text-stone-600 transition-colors whitespace-nowrap">Sign out</button>
              </>
            ) : (
              <>
                <button onClick={() => go('/login')}  className="text-sm text-stone-600 font-medium hover:text-stone-800 transition-colors whitespace-nowrap">Log in</button>
                <button onClick={() => go('/signup')} className="text-sm px-4 py-2 rounded-lg font-semibold text-white whitespace-nowrap" style={{ backgroundColor: '#9E4B2C' }}>Sign up</button>
              </>
            )}
          </div>

          {/* Mobile right */}
          <div className="flex md:hidden items-center gap-2.5">
            <SupportIconButton />
            {user && (
              <button onClick={() => go('/profile')} className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 text-sm font-bold">
                {initial}
              </button>
            )}
            {/* Hamburger */}
            <button onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu" style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: 'var(--space-xs)', background: 'none', border: 'none', cursor: 'pointer' }}>
              <span style={{ display: 'block', width: 22, height: 2, background: 'var(--neutral-800)', borderRadius: 2, transition: 'all 0.25s', transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none' }} />
              <span style={{ display: 'block', width: 22, height: 2, background: 'var(--neutral-800)', borderRadius: 2, transition: 'all 0.25s', opacity: menuOpen ? 0 : 1 }} />
              <span style={{ display: 'block', width: 22, height: 2, background: 'var(--neutral-800)', borderRadius: 2, transition: 'all 0.25s', transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none' }} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Backdrop ──────────────────────────────────────────────────────── */}
      <div
        onClick={() => setMenuOpen(false)}
        style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.4)',
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? 'auto' : 'none',
          transition: 'opacity 0.25s',
        }}
      />

      {/* ── Mobile Drawer ─────────────────────────────────────────────────── */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 300, zIndex: 10001,
        background: 'var(--brand-white)',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
        transform: menuOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
        display: 'flex', flexDirection: 'column',
        fontFamily: 'var(--font-body)',
        overflowY: 'auto',
      }}>

        {/* Close button — top right */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 16px 0' }}>
          <button
            onClick={() => setMenuOpen(false)}
            style={{
              width: 40, height: 40, borderRadius: 'var(--radius-10)',
              border: '1.5px solid var(--brand-sand)', background: 'var(--brand-white)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: 'var(--font-size-xl)', color: 'var(--neutral-600)',
            }}
          >✕</button>
        </div>

        {/* User identity */}
        <div style={{ padding: '20px 24px 28px', display: 'flex', alignItems: 'center', gap: 'var(--space-14)' }}>
          {/* Avatar */}
          <div style={{
            width: 56, height: 56, borderRadius: 'var(--radius-circle)', flexShrink: 0,
            background: profile?.avatar_url ? `url(${profile.avatar_url}) center/cover` : 'var(--feedback-warning-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, fontWeight: 700, color: 'var(--feedback-warning-ink)',
            overflow: 'hidden',
          }}>
            {!profile?.avatar_url && initial}
          </div>
          <div>
            <div style={{ fontSize: 'var(--font-size-xxl)', fontWeight: 700, color: '#1B110D', lineHeight: 1.2 }}>{displayName}</div>
            <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--brand-rust)', fontWeight: 500, marginTop: 'var(--space-xxs)' }}>Reader</div>
          </div>
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: '0 12px' }}>
          {NAV_ITEMS.map(({ label, path, Icon }) => (
            <button
              key={path}
              onClick={() => { if (!okToLeave()) return; setMenuOpen(false); navigate(path); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-lg)',
                width: '100%', padding: '16px 14px',
                background: 'none', border: 'none', borderRadius: 'var(--radius-lg)',
                fontSize: 'var(--font-size-lg)', fontWeight: 500, color: '#1B110D',
                cursor: 'pointer', textAlign: 'left',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#faf8f5'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <span style={{ color: 'var(--neutral-600)', flexShrink: 0 }}><Icon /></span>
              {label}
            </button>
          ))}

          {/* Sign out */}
          {user && (
            <button
              onClick={handleSignOut}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-lg)',
                width: '100%', padding: '16px 14px',
                background: 'none', border: 'none', borderRadius: 'var(--radius-lg)',
                fontSize: 'var(--font-size-lg)', fontWeight: 500, color: 'var(--brand-rust)',
                cursor: 'pointer', textAlign: 'left',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef2ee'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <span style={{ color: 'var(--brand-rust)', flexShrink: 0 }}><IconSignOut /></span>
              Sign out
            </button>
          )}

          {/* Divider */}
          <div style={{ height: 1, background: 'var(--brand-sand)', margin: '8px 14px' }} />

          {/* Are you an author? */}
          <a
            href="https://reanloforauthors.com"
            target="_blank" rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-lg)',
              width: '100%', padding: '16px 14px',
              borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-lg)', fontWeight: 500,
              color: 'var(--brand-rust)', textDecoration: 'none',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#fef2ee'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <span style={{ color: 'var(--brand-rust)', flexShrink: 0 }}><IconPen /></span>
            Are you an author? →
          </a>
        </nav>

        {/* Bottom — email + sign out button */}
        {user && (
          <div style={{ padding: '16px 24px 48px', borderTop: '1px solid var(--brand-sand)' }}>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: '0 0 12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.email}
            </p>
            <button
              onClick={handleSignOut}
              style={{
                width: '100%', padding: 'var(--space-14)', borderRadius: 'var(--radius-lg)',
                border: '1.5px solid var(--brand-rust)', background: 'var(--brand-white)',
                fontSize: 15, fontWeight: 600, color: 'var(--brand-rust)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)',
              }}
            >
              <IconSignOut /> Sign out
            </button>
          </div>
        )}

        {/* Not logged in bottom */}
        {!user && (
          <div style={{ padding: '16px 24px 48px', borderTop: '1px solid var(--brand-sand)', display: 'flex', flexDirection: 'column', gap: 'var(--space-10)' }}>
            <button onClick={() => { if (!okToLeave()) return; setMenuOpen(false); navigate('/login'); }}  style={{ width: '100%', padding: 'var(--space-14)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)', cursor: 'pointer' }}>Log in</button>
            <button onClick={() => { if (!okToLeave()) return; setMenuOpen(false); navigate('/signup'); }} style={{ width: '100%', padding: 'var(--space-14)', borderRadius: 'var(--radius-lg)', border: 'none', background: '#9E4B2C', fontSize: 15, fontWeight: 600, color: 'var(--brand-white)', cursor: 'pointer' }}>Sign up free</button>
          </div>
        )}
      </div>

    </>
  );
}